/**
 * A CÉGVÁLASZTÓ SORRENDJE (#73) — mit lát a könyvelő, és mit ad az `Enter`.
 *
 * Több száz ügyfélnél a választó nem görgetős lista, hanem gépelős kereső.
 * A sorrend azt követi, amit a könyvelő begépel:
 *
 *   1. a PONTOS Tensoft-kód (`PI` → Pierre) — a Tensoftból hozott szokás,
 *      két leütés és `Enter`;
 *   2. a név, szavanként és ékezet nélkül (`alf epito`), mint a többi keresőnk;
 *   3. az adószám töredéke (`1234`) — legalább három számjegytől, különben
 *      minden cég találat volna.
 *
 * Üres keresésnél a legutóbbiak állnak elöl, mert a napi munka néhány cég
 * körül forog. Tiszta függvény, React nélkül.
 */

import { fold, matchesQuery } from '@/utils/fold';

export interface PickerCompany {
  id: string;
  name: string;
  taxNumber: string;
  tensoftCode: string | null;
}

/** Az adószám-keresés alsó határa, számjegyben. */
const MIN_TAX_DIGITS = 3;

function digits(value: string): string {
  return value.replace(/\D/g, '');
}

export function rankCompanies(
  companies: readonly PickerCompany[],
  query: string,
  recent: readonly string[]
): PickerCompany[] {
  const trimmed = query.trim();

  if (trimmed === '') {
    const byId = new Map(companies.map((company) => [company.id, company]));
    const first = recent.flatMap((id) => byId.get(id) ?? []);
    const shown = new Set(first.map((company) => company.id));
    return [...first, ...companies.filter((company) => !shown.has(company.id))];
  }

  const code = fold(trimmed);
  const queryDigits = digits(trimmed);
  const byCode = companies.filter(
    (company) => company.tensoftCode !== null && fold(company.tensoftCode) === code
  );
  const byName = companies.filter((company) => matchesQuery(company.name, trimmed));
  const byTax =
    queryDigits.length >= MIN_TAX_DIGITS
      ? companies.filter((company) => digits(company.taxNumber).includes(queryDigits))
      : [];

  const seen = new Set<string>();
  return [...byCode, ...byName, ...byTax].filter((company) => {
    if (seen.has(company.id)) {
      return false;
    }
    seen.add(company.id);
    return true;
  });
}
